import { booleanAttribute } from "#ce/utils/props";
import { sizeProps } from "#ce/composables/useSize";
import { systemColors } from "#ce/composables/color";

export const LAYOUTS = ["row", "row-reverse", "column", "column-reverse"];
export const ALIGNMENTS = ["start", "center", "end"];
export const COLORS = [...systemColors, "error", "custom"];
const { size, sizeSm } = sizeProps;

// We had bugs in the past, where the props were mutatated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Icon size override for the info card icon on desktop and larger screens. Leave undefined to use the variant defaults.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   */
  size: {
    ...size,
  },

  /**
   * Icon size override for the info card icon on small screens. Leave undefined to use the variant defaults.
   *
   * @type {String}
   * @default undefined
   * @storybook-control select
   * @storybook-table-subcategory Small Screen
   */
  sizeSm: {
    ...sizeSm,
  },
  /**
   * Visual variant. "default" uses the surface background; "neutral" uses a tinted surface; "inverted" swaps to a coloured background driven by the `color` prop.
   *
   * @type {String}
   * @default "default"
   * @values default, neutral, inverted
   * @storybook-control radio
   */
  variant: {
    type: String,
    default: "default",
    validator: (value) => ["default", "neutral", "inverted"].includes(value),
  },

  /**
   * Icon name to display in the card. Set to `false` to hide the icon.
   *
   * @type {String|Boolean}
   * @default false
   * @storybook-control select
   */
  icon: {
    type: [String, Boolean],
    default: false,
  },

  /**
   * Title text of the info card.
   *
   * @type {String}
   * @default undefined
   */
  title: {
    type: String,
    required: true,
    default: undefined,
  },

  /**
   * Body content text of the info card.
   *
   * @type {String}
   * @default undefined
   */
  content: {
    type: String,
    default: undefined,
  },

  /**
   * URL for the link overlay. When set, the entire card becomes clickable.
   *
   * @type {String}
   * @default undefined
   */
  href: {
    type: String,
    default: undefined,
  },

  /**
   * Target attribute for the link overlay (e.g. "_blank", "_self").
   *
   * @type {String}
   * @default undefined
   */
  target: {
    type: String,
    default: undefined,
  },

  /**
   * Layout direction of the card header (icon + text). "column" stacks the icon above the text; "row" places them side by side; the "-reverse" variants flip the order.
   *
   * @type {String}
   * @default "column"
   * @values row, row-reverse, column, column-reverse
   * @storybook-control radio
   */
  layout: {
    type: String,
    default: "column",
    validator: (value) => LAYOUTS.includes(value),
  },

  /**
   * Layout direction of the card header on small screens. Leave undefined to inherit `layout`.
   *
   * @type {String}
   * @default undefined
   * @values row, row-reverse, column, column-reverse
   * @storybook-control radio
   * @storybook-table-subcategory Small Screen
   */
  layoutSm: {
    type: String,
    default: undefined,
    validator: (value) => LAYOUTS.includes(value),
  },

  /**
   * Alignment of the icon and text content along the cross axis.
   *
   * @type {String}
   * @default "start"
   * @values start, center, end
   * @storybook-control radio
   */
  align: {
    type: String,
    default: "start",
    validator: (value) => ALIGNMENTS.includes(value),
  },

  /**
   * Alignment of the icon and text content on small screens. Leave undefined to inherit `align`.
   *
   * @type {String}
   * @default undefined
   * @values start, center, end
   * @storybook-control radio
   * @storybook-table-subcategory Small Screen
   */
  alignSm: {
    type: String,
    default: undefined,
    validator: (value) => ALIGNMENTS.includes(value),
  },

  /**
   * Theme colour for the inverted variant's background. Ignored when `variant` is "default" or "neutral".
   *
   * @type {String}
   * @default "primary"
   * @storybook-control select
   */
  color: {
    type: String,
    default: "primary",
    validator: (value) => COLORS.includes(value),
  },

  /**
   * Minimum height of the card.
   *
   * @type {String|Number}
   * @default 0
   */
  minHeight: {
    type: [String, Number],
    default: 0,
  },

  /**
   * Minimum height of the card on small screens.
   *
   * @type {String|Number}
   * @default 0
   * @storybook-table-subcategory Small Screen
   */
  minHeightSm: {
    type: [String, Number],
    default: 0,
  },

  /**
   * Height of the card. Supports px, rem, %, and unitless numbers (converted to rem).
   *
   * @type {String|Number}
   * @default undefined
   */
  height: {
    type: [String, Number],
    default: undefined,
  },

  /**
   * Height of the card on small screens. Supports px, rem, %, and unitless numbers (converted to rem).
   *
   * @type {String|Number}
   * @default undefined
   * @storybook-table-subcategory Small Screen
   */
  heightSm: {
    type: [String, Number],
    default: undefined,
  },

  /**
   * Stretch the card to fill the height of its container.
   *
   * @type {Boolean|String}
   * @default false
   */
  fillHeight: booleanAttribute(),
});
